/**
 * Derives the UI's `kind` / `format` for a column from the backend's raw
 * source `data_type`.
 *
 * Why this exists: the v2 columns API sends no semantic type. It sends
 * `data_type` — the underlying source type verbatim, e.g. `integer`,
 * `varchar(255)`, `NUMBER(38,0)`, `TIMESTAMP_NTZ` — plus
 * `is_primary_key`/`is_foreign_key`. The previous contract had the backend
 * classify columns for us (`type: "ID" | "CATEGORIAL" | ...`); that field does
 * not exist. So the classification moves here.
 *
 * `data_type` strings vary across the 38 connector types, so matching is
 * normalised (lower-cased, precision/scale suffix and array brackets stripped)
 * and falls back to `string` on anything unrecognised rather than throwing —
 * an unknown type must still render a column.
 *
 * See `DATA_STUDIO_API_ALIGNMENT_PLAN.md` §7 Q3: a canonical `data_type`
 * vocabulary is still outstanding with the backend. Every unrecognised type is
 * reported in dev via `onUnknownDataType` so the gaps surface during QA.
 */
import type {
  DataStudioColumnFormat,
  DataStudioColumnKind,
} from "./dataStudioTables.types";

export interface ColumnTypeClassification {
  kind: DataStudioColumnKind;
  format: DataStudioColumnFormat;
}

/** The column flags that outrank `data_type` when classifying. */
export interface ColumnTypeClassifierInput {
  data_type: string;
  is_primary_key: boolean;
  is_foreign_key: boolean;
}

/**
 * Optional cardinality hint used to separate a low-cardinality categorical
 * column (`dimension` — rendered as a donut/bar) from free text (`string`).
 *
 * The API has no categorical flag, so without this hint every text column
 * renders identically. Derived from `profiling_summary` when the backend sent
 * one; omit it and text columns classify as `string`.
 */
export interface ColumnCardinalityHint {
  distinctCount: number | null;
  totalCount: number | null;
}

/**
 * A text column is treated as categorical below this distinct-to-total ratio.
 *
 * ASSUMPTION, not from the spec — chosen so a status/region/segment column
 * reads as a dimension while a name or free-text note does not. Exported so it
 * can be tuned in one place once product confirms the banding
 * (`DATA_STUDIO_API_ALIGNMENT_PLAN.md` §7 Q3).
 */
export const CATEGORICAL_DISTINCT_RATIO = 0.2;

/** Absolute distinct-value ceiling for the categorical inference above. */
export const CATEGORICAL_DISTINCT_CEILING = 50;

const INTEGER_TYPES = new Set([
  "int",
  "int2",
  "int4",
  "int8",
  "int16",
  "int32",
  "int64",
  "integer",
  "smallint",
  "mediumint",
  "bigint",
  "tinyint",
  "serial",
  "smallserial",
  "bigserial",
  "long",
  "byteint",
]);

const NUMERIC_TYPES = new Set([
  "numeric",
  "number",
  "decimal",
  "dec",
  "bignumeric",
  "float",
  "float4",
  "float8",
  "float64",
  "double",
  "double precision",
  "real",
  "money",
  "smallmoney",
  "currency",
]);

const BOOLEAN_TYPES = new Set(["bool", "boolean", "bit", "logical"]);

const DATETIME_TYPES = new Set([
  "date",
  "time",
  "timetz",
  "time with time zone",
  "time without time zone",
  "datetime",
  "datetime2",
  "datetimeoffset",
  "smalldatetime",
  "timestamp",
  "timestamptz",
  "timestamp_ntz",
  "timestamp_ltz",
  "timestamp_tz",
  "timestamp with time zone",
  "timestamp without time zone",
  "interval",
  "year",
]);

const GEO_TYPES = new Set([
  "geography",
  "geometry",
  "point",
  "line",
  "lseg",
  "box",
  "path",
  "polygon",
  "circle",
  "st_geometry",
]);

const STRING_TYPES = new Set([
  "char",
  "character",
  "character varying",
  "varchar",
  "varchar2",
  "nchar",
  "nvarchar",
  "ntext",
  "text",
  "string",
  "longtext",
  "mediumtext",
  "tinytext",
  "clob",
  "uuid",
  "uniqueidentifier",
  "json",
  "jsonb",
  "variant",
  "object",
  "array",
  "xml",
  "enum",
  "binary",
  "varbinary",
  "bytea",
  "blob",
]);

/**
 * Strips the parts of a source type that carry no classification signal:
 * case, surrounding whitespace, array markers, and the precision/scale suffix.
 * `NUMBER(38,0)` → `number`, `VARCHAR(255)` → `varchar`, `int4[]` → `int4`.
 */
export const normalizeDataType = (dataType: string): string =>
  dataType
    .trim()
    .toLowerCase()
    .replace(/\[\s*\d*\s*\]/g, "")
    .replace(/\([^)]*\)/g, "")
    .replace(/\s+/g, " ")
    .trim();

/**
 * Reads the scale from a numeric type's precision/scale suffix.
 * `number(38,0)` → 0, `decimal(10,2)` → 2, `numeric` → null (unspecified).
 *
 * Scale 0 means the source stores whole numbers — Snowflake's `NUMBER(38,0)`
 * and BigQuery's `NUMERIC(38,0)` are how those warehouses spell an integer —
 * so it classifies as `integer`, not `numeric`. Without this, integer id-like
 * columns would get a continuous histogram instead of a discrete chart.
 */
export const readNumericScale = (dataType: string): number | null => {
  const match = dataType.match(/\(\s*\d+\s*,\s*(\d+)\s*\)/);
  if (!match) return null;
  const scale = Number(match[1]);
  return Number.isFinite(scale) ? scale : null;
};

const KIND_TO_DEFAULT_FORMAT: Record<
  DataStudioColumnKind,
  DataStudioColumnFormat
> = {
  id: "id",
  dimension: "categorical",
  string: "text",
  integer: "integer",
  numeric: "numeric",
  datetime: "datetime",
  geo: "region",
  boolean: "boolean",
};

/** The format a `kind` renders with when nothing finer is known. */
export const defaultFormatForKind = (
  kind: DataStudioColumnKind,
): DataStudioColumnFormat => KIND_TO_DEFAULT_FORMAT[kind];

/**
 * True when the hint indicates few enough distinct values to read as a
 * category rather than free text. Requires both counts — a distinct count
 * alone says nothing about cardinality.
 */
export const isLowCardinality = (hint?: ColumnCardinalityHint): boolean => {
  const distinct = hint?.distinctCount;
  const total = hint?.totalCount;
  if (typeof distinct !== "number" || typeof total !== "number") return false;
  if (distinct <= 0 || total <= 0) return false;
  if (distinct > CATEGORICAL_DISTINCT_CEILING) return false;
  return distinct / total <= CATEGORICAL_DISTINCT_RATIO;
};

/** Maps a normalised source type to a `kind`, or `null` when unrecognised. */
const kindForNormalizedType = (
  normalized: string,
): DataStudioColumnKind | null => {
  if (INTEGER_TYPES.has(normalized)) return "integer";
  if (NUMERIC_TYPES.has(normalized)) return "numeric";
  if (BOOLEAN_TYPES.has(normalized)) return "boolean";
  if (DATETIME_TYPES.has(normalized)) return "datetime";
  if (GEO_TYPES.has(normalized)) return "geo";
  if (STRING_TYPES.has(normalized)) return "string";
  return null;
};

export interface ClassifyColumnTypeOptions {
  cardinality?: ColumnCardinalityHint;
  /** Called with any `data_type` that fell through to the `string` default. */
  onUnknownDataType?: (dataType: string) => void;
}

/**
 * Classifies a column into the `kind`/`format` pair the grid renders from.
 *
 * Precedence, highest first:
 * 1. `is_primary_key` / `is_foreign_key` → `id`. A key's chart and statistics
 *    are driven by its role, not by whether it is stored as an int or a uuid.
 * 2. The normalised `data_type`, with numeric scale 0 narrowed to `integer`.
 * 3. Low cardinality on a text column → `dimension`.
 * 4. `string` as the fallback for an unrecognised type.
 */
export const classifyColumnType = (
  column: ColumnTypeClassifierInput,
  options: ClassifyColumnTypeOptions = {},
): ColumnTypeClassification => {
  if (column.is_primary_key || column.is_foreign_key) {
    return { kind: "id", format: "id" };
  }

  const normalized = normalizeDataType(column.data_type);
  const matched = kindForNormalizedType(normalized);

  if (matched === "numeric" && readNumericScale(column.data_type) === 0) {
    return { kind: "integer", format: "integer" };
  }

  if (matched === null) {
    options.onUnknownDataType?.(column.data_type);
  }

  const kind = matched ?? "string";

  if (kind === "string" && isLowCardinality(options.cardinality)) {
    return { kind: "dimension", format: "categorical" };
  }

  return { kind, format: defaultFormatForKind(kind) };
};
